/** 画像Blobと手書きストロークのRepository（仕様書 §6・§7.7）。 */

import { getDb, type MediaRecord, type StrokeRecord } from './schema';
import type { MonthKey, StrokeDoc } from '../domain/types';
import { enqueue } from './outbox';
import { bumpData } from './events';

export async function getMediaBlob(id: string): Promise<Blob | undefined> {
  const db = await getDb();
  const rec = await db.get('media', id);
  if (!rec) return undefined;
  // LRU の判定に使う最終アクセス時刻を更新する
  await db.put('media', { ...rec, lastAccessAt: Date.now() });
  return rec.blob;
}

/**
 * ローカルに保存する。アップロードのキュー投入は行わない。
 * 記事が保存される前にキューへ入れると、破棄された記事の画像まで送ってしまうため。
 */
export async function putMedia(rec: Omit<MediaRecord, 'lastAccessAt'>): Promise<void> {
  const db = await getDb();
  await db.put('media', { ...rec, lastAccessAt: Date.now() });
}

/** 記事の保存が確定した時点で、未アップロードのメディアをキューへ投入する。 */
export async function enqueueMediaUploads(entryId: string, month: MonthKey): Promise<void> {
  const db = await getDb();
  const items = await db.getAllFromIndex('media', 'by_entry', entryId);
  for (const m of items) {
    if (m._uploaded) continue;
    if (m.month !== month) await db.put('media', { ...m, month });
    await enqueue({ type: 'media.put', targetKey: `media:${m.id}`, entryId, month });
  }
}

export async function getStrokes(id: string): Promise<StrokeDoc | undefined> {
  const db = await getDb();
  return (await db.get('strokes', id))?.data;
}

export async function putStrokes(rec: StrokeRecord): Promise<void> {
  const db = await getDb();
  await db.put('strokes', rec);
}

export async function deleteMedia(id: string, month: MonthKey): Promise<void> {
  const db = await getDb();
  await db.delete('media', id);
  await db.delete('strokes', id);
  await enqueue({ type: 'media.delete', targetKey: `media:${id}`, month });
  bumpData();
}

/**
 * 記事に添付された写真のうち、EXIF の位置情報を持つ最初のものを返す（§11.1）。
 * media の並び順（＝表示順）で走査する。
 */
export async function findExifGps(
  mediaIds: string[],
): Promise<{ lat: number; lng: number } | null> {
  const db = await getDb();
  for (const id of mediaIds) {
    const rec = await db.get('media', id);
    if (rec?.exifGps) return rec.exifGps;
  }
  return null;
}

/** 記事の破棄時に、紐づくメディアをまとめて消す（未保存の新規記事の後始末）。 */
export async function discardMediaForEntry(entryId: string): Promise<void> {
  const db = await getDb();
  const mediaKeys = await db.getAllKeysFromIndex('media', 'by_entry', entryId);
  const strokeKeys = await db.getAllKeysFromIndex('strokes', 'by_entry', entryId);
  await Promise.all([
    ...mediaKeys.map((k) => db.delete('media', k)),
    ...strokeKeys.map((k) => db.delete('strokes', k)),
  ]);
}
